import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';
import catBoardingHeroImageDesktop from '../shared-images/Hero_Cat_desktop.jpg';
import catBoardingHeroImageMobile from '../shared-images/Hero_Cat_mobile.jpg';
import catBoardingHeroImageTablet from '../shared-images/Hero_Cat_tablet.jpg';

const catBoardingImages: HeroImageSrcSet = {
  desktop: {
    src: catBoardingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: catBoardingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: catBoardingHeroImageMobile,
    height: 614,
  },
};

const CatBoarding: React.FC<Props> = ({
  language,
  citiesAndNeighborhoods,
  serviceLinks,
  serviceLinksWithDisplayName,
  serviceLandingData,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
  countryConfig,
}) => {
  const serviceLandingType = ServiceLandingEnum.CAT_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x" heroImageSrcSet={catBoardingImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Finally—a cat boarding option that works for cats</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Discover the right care for your cat</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Whether you choose cat boarding, drop-in visits, or house sitting, your cat will
                    get all the love and care they need in a comfortable home environment.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Meet trusted local cat sitters</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every cat sitter profile has been individually reviewed and approved by Rover.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Get to know your sitter with a Meet & Greet</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Schedule a free in-person Meet & Greet with your cat boarding sitter to make
                    sure they’re a good fit before you book.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Enjoy adorable photo updates</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Millions of pet pics have been sent through Rover. Get photos of your cat’s
                    adventures from your cat boarding sitter while you’re away.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>

              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Find your cat’s perfect fit on Rover</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Your cat is one of a kind—book cat boarding with a sitter who gets it. Meet the
                    right person for you, your cat, and your budget.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
            <ServiceValueProposition.Subtitle>
              <Trans>
                You’ll find pet parents, pet sitters, and dog walkers who use Rover in thousands of
                cities worldwide—join us!
              </Trans>
            </ServiceValueProposition.Subtitle>
          </ServiceValueProposition>
          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Browse to book in 3 steps flat</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Browse cat sitter profiles and reach out to ones you like.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    It’s that easy! All payments are processed through Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Relax</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Your booking is backed by the Rover Guarantee, reservation protection, and 24/7
                    support.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>
          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />
          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Find the perfect cat sitter on Rover</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content marginBottom="0x">
              <Trans>
                Whether you’ll be away for a weekend or a whole week, we make it easy to find the
                right cat sitter for short and long-term cat boarding. Your sitter will give your
                cat plenty of one-on-one attention, caring for them just like you would in their
                cat-friendly home. Rover cat boarding is a great cat kennel alternative that’s less
                stressful for you and your cat. Does your cat prefer to stay on their own turf?
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.Content>
              <Trans>
                Try{' '}
                <Link
                  href={serviceLinks[ServiceLandingEnum.CAT_SITTING]}
                  target="_blank"
                  rel="noreferrer"
                >
                  drop-in visits
                </Link>{' '}
                or house sitting, where the sitter comes to your home.
              </Trans>
            </ServiceTargetAudience.Content>
          </ServiceTargetAudience>

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>
          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default CatBoarding;
